import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { XCircle, Send, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { PM_EMAIL_TEMPLATES, generateProManagerEmailHTML } from '../../utils/proManagerEmailGenerator';

const API = process.env.REACT_APP_BACKEND_URL;

export const SendEmailModal = ({ customer, initialTemplate, onClose, onSent }) => {
  const startKey = PM_EMAIL_TEMPLATES[initialTemplate] ? initialTemplate : 'follow_up';
  const [templateKey, setTemplateKey] = useState(startKey);
  const [toEmail, setToEmail] = useState(customer.email || '');
  const [senderEmail, setSenderEmail] = useState('');
  const [subject, setSubject] = useState(PM_EMAIL_TEMPLATES[startKey].subject(customer));
  const [message, setMessage] = useState(PM_EMAIL_TEMPLATES[startKey].defaultMessage(customer));
  const [depositAmount, setDepositAmount] = useState(customer.deposit_paid > 0 ? String(customer.deposit_paid) : '95');
  const [senderAccounts, setSenderAccounts] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/sender-accounts`)
      .then((r) => {
        const accounts = r.data.accounts || [];
        setSenderAccounts(accounts);
        const preferred = accounts.find((a) => a.connected) || accounts[0];
        if (preferred) setSenderEmail(preferred.email);
      })
      .catch(() => setSenderAccounts([]));
  }, []);

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    setSubject(PM_EMAIL_TEMPLATES[key].subject(customer));
    setMessage(PM_EMAIL_TEMPLATES[key].defaultMessage(customer));
  };

  const html = useMemo(
    () => generateProManagerEmailHTML({ templateKey, customer, message, depositAmount }),
    [templateKey, customer, message, depositAmount]
  );

  const senderConnected = senderAccounts === null || (senderAccounts.find((a) => a.email === senderEmail)?.connected ?? false);

  const handleSend = async () => {
    if (!toEmail || !toEmail.includes('@')) { toast.error('Enter a valid recipient email'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/send-email`, {
        to_email: toEmail,
        subject,
        html_content: html,
        sender_email: senderEmail,
      });
      let logEntry = { email_type: templateKey, subject, sender_email: senderEmail, to_email: toEmail, source: 'pro_manager', sent_at: new Date().toISOString() };
      try {
        const { data } = await axios.post(`${API}/api/pro-manager/customers/${customer.id}/email-log`, {
          email_type: templateKey, subject, sender_email: senderEmail, to_email: toEmail, source: 'pro_manager', html_content: html,
        });
        if (data.entry) logEntry = data.entry;
      } catch { /* logging failure shouldn't block the send flow */ }
      toast.success(`${PM_EMAIL_TEMPLATES[templateKey].label} email sent to ${customer.name}!`);
      onSent?.(logEntry);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4" data-testid="pm-send-email-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-zinc-800 p-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Send className="w-4 h-4 text-amber-500" /> Send Email — {customer.name}</h2>
            <p className="text-xs text-zinc-500">Review the pre-filled email below, then hit Send Now</p>
          </div>
          <button onClick={onClose} data-testid="pm-send-email-close" className="p-2 hover:bg-zinc-800 rounded-lg">
            <XCircle className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto grid md:grid-cols-2 gap-0">
          {/* Left: editable fields */}
          <div className="p-4 space-y-3 border-r border-zinc-800">
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Email Type</label>
              <select value={templateKey} onChange={(e) => handleTemplateChange(e.target.value)} data-testid="pm-email-template-select"
                className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none">
                {Object.entries(PM_EMAIL_TEMPLATES).map(([key, t]) => (
                  <option key={key} value={key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">To</label>
                <input value={toEmail} onChange={(e) => setToEmail(e.target.value)} data-testid="pm-email-to-input"
                  className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none" placeholder="customer@email.com" />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Send From</label>
                <select value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} data-testid="pm-email-sender-select"
                  className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none">
                  {(senderAccounts || []).map((a) => <option key={a.id} value={a.email}>{a.email}{a.connected ? '' : ' (not connected)'}</option>)}
                </select>
              </div>
            </div>
            {!senderConnected && (
              <div className="flex items-center gap-2 bg-red-950/60 border border-red-800 rounded-lg px-3 py-2 text-xs text-red-300" data-testid="pm-sender-not-connected-warning">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {senderEmail} is not connected to Gmail. Ask Head Office to connect it from the Email Builder.
              </div>
            )}
            {senderConnected && senderAccounts !== null && senderEmail && (
              <div className="flex items-center gap-2 text-xs text-emerald-400"><CheckCircle className="w-3.5 h-3.5" /> {senderEmail} connected</div>
            )}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Subject</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pm-email-subject-input"
                className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none" />
            </div>
            {templateKey === 'deposit_request' && (
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Deposit Amount (£)</label>
                <input type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} data-testid="pm-email-deposit-amount-input"
                  className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none" />
                <p className="text-[10px] text-zinc-600 mt-1">Bank details are included automatically in the email</p>
              </div>
            )}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Message (edit freely — formatting is applied automatically)</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={12} data-testid="pm-email-message-textarea"
                className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg text-sm border border-zinc-700 focus:border-amber-500 outline-none resize-y" />
            </div>
          </div>

          {/* Right: live preview */}
          <div className="bg-zinc-950 p-4 hidden md:block">
            <p className="text-xs text-zinc-500 mb-2">Live Preview</p>
            <div className="bg-white rounded-lg overflow-hidden h-[calc(100%-24px)]">
              <iframe title="email-preview" srcDoc={html} className="w-full h-full border-0" data-testid="pm-email-preview-iframe" />
            </div>
          </div>
        </div>

        <div className="border-t border-zinc-800 p-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-xl text-sm font-medium hover:bg-zinc-700">
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending || !toEmail || !senderConnected} data-testid="pm-email-send-now-button"
            className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-xl font-bold text-sm hover:from-amber-400 hover:to-amber-500 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
};
